import {Group, Mesh, MeshBasicMaterial, Object3D, Vector3} from 'three';

import {checkExhaustive} from 'external/dev_april_corgi+/js/common/asserts';

// Defines a quasi-flexbox layout scheme. Insets act like absolutely positioned elements inside
// their parent (as if every parent is marked relative.)
export interface ObjectTree {
  kind: 'tree';
  basis?: {
    height: number;
    width: number;
  };
  // Defaults to vertical
  direction?: 'horizontal' | 'vertical';
  gap?: number;
  // Defaults to start
  gravity?: 'start' | 'center' | 'end';
  grow?: number;
  children?: Array<ObjectTree | ObjectLeaf>;
  insets?: Array<ObjectTree | ObjectLeaf>;
}

export interface ObjectLeaf {
  kind: 'leaf';
  object: Object3D;
  notify?(size: {height: number; width: number}): void;
  basis?: {
    height: number;
    width: number;
  };
  grow?: number;
}

export function layout(object: ObjectLeaf | ObjectTree): void {
  measure(object);
  size(object, object.basis ?? {height: 0, width: 0});
  distribute(object, new Vector3(0, 0));
}

// Measure the minimal size of every object
function measure(object: ObjectLeaf | ObjectTree): void {
  if (object.kind === 'leaf') {
    return;
  }

  const growing = object.direction === 'horizontal' ? 'width' : 'height';
  const fixed = object.direction === 'horizontal' ? 'height' : 'width';
  const need = {height: 0, width: 0};
  const gap = object.gap ?? 0;
  for (const child of object.children ?? []) {
    measure(child);
    const basis = child.basis ?? {height: 0, width: 0};
    need[growing] += basis[growing] + gap;
    need[fixed] = Math.max(need[fixed], basis[fixed]);
  }
  need[growing] -= gap;

  object.basis = need;
}

// Given a bunch of measured objects, distribute the space between them
function size(
  object: ObjectLeaf | ObjectTree,
  bound: {height: number; width: number},
): void {
  if (object.kind === 'leaf') {
    object.basis = bound;
    return;
  }

  const growing = object.direction === 'horizontal' ? 'width' : 'height';
  const fixed = object.direction === 'horizontal' ? 'height' : 'width';
  const need = {height: 0, width: 0};
  const gap = object.gap ?? 0;
  let requests = 0;
  for (const child of object.children ?? []) {
    const basis = child.basis ?? {height: 0, width: 0};
    need[growing] += basis[growing] + gap;
    need[fixed] = Math.max(need[fixed], basis[fixed]);
    requests += child.grow ?? 0;
  }
  need[growing] -= gap;
  const available = bound[growing] - need[growing];
  for (const child of object.children ?? []) {
    const basis = child.basis ?? {height: 0, width: 0};
    const grow = child.grow ?? 0;
    size(child, {
      [growing]:
        basis[growing] + (grow > 0 ? (grow / requests) * available : 0),
      [fixed]: bound[fixed],
    } as {height: number; width: number});
  }
  object.basis = bound;

  for (const inset of object.insets ?? []) {
    size(inset, bound);
  }
}

function distribute(object: ObjectTree | ObjectLeaf, center: Vector3): void {
  const basis = object.basis ?? {height: 0, width: 0};
  if (object.kind === 'leaf') {
    object.object.position.x = center.x;
    object.object.position.y = -center.y;
    object.notify?.(basis);
    return;
  }

  const gap = object.gap ?? 0;
  const extra = {...basis};
  const direction = object.direction === 'horizontal' ? 'width' : 'height';
  for (const child of object.children ?? []) {
    const basis = child.basis ?? {height: 0, width: 0};
    extra[direction] -= basis[direction] + gap;
  }
  extra[direction] += gap;

  let offset;
  if (!object.gravity || object.gravity === 'start') {
    offset = -basis[direction] / 2;
  } else if (object.gravity === 'center') {
    offset = -basis[direction] / 2 + extra[direction] / 2;
  } else if (object.gravity === 'end') {
    offset = -basis[direction] / 2 + extra[direction];
  } else {
    throw checkExhaustive(object.gravity);
  }

  for (const child of object.children ?? []) {
    const basis = child.basis ?? {height: 0, width: 0};
    const theirCenter = new Vector3(
      center.x +
        (object.direction === 'horizontal' ? offset + basis.width / 2 : 0),
      center.y +
        (object.direction !== 'horizontal' ? offset + basis.height / 2 : 0),
      center.z,
    );
    distribute(child, theirCenter);
    offset += basis[direction] + gap;
  }

  for (const inset of object.insets ?? []) {
    distribute(inset, center);
  }
}

export function addAndPruneMeshes(
  layout: ObjectLeaf | ObjectTree,
  have: Set<Mesh>,
  group: Group,
): void {
  const need = gatherMeshes(layout, new Set());
  for (const mesh of need) {
    if (!have.has(mesh)) {
      group.add(mesh);
      have.add(mesh);
    }
  }
  for (const mesh of have) {
    if (!need.has(mesh)) {
      removeAndDispose(mesh);
    }
  }
}

function gatherMeshes(
  object: ObjectLeaf | ObjectTree,
  into: Set<Mesh>,
): Set<Mesh> {
  if (object.kind === 'leaf') {
    if (object.object instanceof Mesh) {
      into.add(object.object);
    }
  } else {
    for (const child of object.children ?? []) {
      gatherMeshes(child, into);
    }
    for (const inset of object.insets ?? []) {
      gatherMeshes(inset, into);
    }
  }
  return into;
}

export function removeAndDispose(mesh: Mesh): void {
  mesh.removeFromParent();
  mesh.geometry.dispose();
  const mat = mesh.material;
  const materials = Array.isArray(mat) ? mat : [mat];
  for (const material of materials) {
    if (material instanceof MeshBasicMaterial) {
      material.map?.dispose();
    }
    material.dispose();
  }
}
